import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  UserX,
  UserCheck,
  AlertTriangle,
  Skull,
  LogOut,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import { patientsApi } from '@/api/endpoints/patients.api';
import type { PatientDetail, InactivationReason } from '@/types/patient.types';
import { InactivationReasonLabels, ProgramStatusLabels } from '@/types/patient.types';

interface PatientStatusCardProps {
  patient: PatientDetail;
}

export function PatientStatusCard({ patient }: PatientStatusCardProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [showInactivateDialog, setShowInactivateDialog] = useState(false);
  const [showDeceasedDialog, setShowDeceasedDialog] = useState(false);
  const [showDischargeDialog, setShowDischargeDialog] = useState(false);
  const [showReactivateConfirm, setShowReactivateConfirm] = useState(false);

  const [inactivationReason, setInactivationReason] = useState<InactivationReason>('OTHER');
  const [notes, setNotes] = useState('');
  const [deceasedDate, setDeceasedDate] = useState('');

  const inactivateMutation = useMutation({
    mutationFn: (data: { reason: InactivationReason; notes?: string }) =>
      patientsApi.inactivate(patient.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient', patient.id] });
      toast({ title: 'Paciente inativado com sucesso' });
      setShowInactivateDialog(false);
      resetForm();
    },
    onError: () => {
      toast({ title: 'Erro ao inativar paciente', variant: 'destructive' });
    },
  });

  const markDeceasedMutation = useMutation({
    mutationFn: (data: { deceasedAt?: string; notes?: string }) =>
      patientsApi.markDeceased(patient.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient', patient.id] });
      toast({ title: 'Obito registrado com sucesso' });
      setShowDeceasedDialog(false);
      resetForm();
    },
    onError: () => {
      toast({ title: 'Erro ao registrar obito', variant: 'destructive' });
    },
  });

  const dischargeMutation = useMutation({
    mutationFn: (data: { notes?: string }) =>
      patientsApi.discharge(patient.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient', patient.id] });
      toast({ title: 'Paciente desligado com sucesso' });
      setShowDischargeDialog(false);
      resetForm();
    },
    onError: () => {
      toast({ title: 'Erro ao desligar paciente', variant: 'destructive' });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: () => patientsApi.reactivate(patient.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient', patient.id] });
      toast({ title: 'Paciente reativado com sucesso' });
      setShowReactivateConfirm(false);
    },
    onError: () => {
      toast({ title: 'Erro ao reativar paciente', variant: 'destructive' });
    },
  });

  const resetForm = () => {
    setInactivationReason('OTHER');
    setNotes('');
    setDeceasedDate('');
  };

  const getStatusBadge = () => {
    if (patient.deceasedAt) {
      return <Badge variant="destructive">Falecido</Badge>;
    }
    if (patient.programStatus === 'INACTIVE') {
      return <Badge variant="secondary">Inativo</Badge>;
    }
    if (patient.programStatus === 'DISCHARGED') {
      return <Badge variant="outline">Desligado (Alta)</Badge>;
    }
    if (patient.programStatus === 'ACTIVE') {
      return <Badge variant="default">Ativo</Badge>;
    }
    if (patient.programStatus === 'CAPTATION') {
      return <Badge className="bg-blue-500">Em Captacao</Badge>;
    }
    return <Badge variant="outline">{ProgramStatusLabels[patient.programStatus || 'ELIGIBLE']}</Badge>;
  };

  const canBeInactivated = patient.programStatus === 'ACTIVE' || patient.programStatus === 'CAPTATION';
  const canBeReactivated = (patient.programStatus === 'INACTIVE' || patient.programStatus === 'DISCHARGED') && !patient.deceasedAt;
  const canBeDischarged = patient.programStatus === 'ACTIVE' && !patient.deceasedAt;

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span>Status do Paciente</span>
            {getStatusBadge()}
          </CardTitle>
          {patient.programStatus && (
            <CardDescription>
              {ProgramStatusLabels[patient.programStatus]}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Informacoes de inativacao */}
          {patient.inactivationReason && (
            <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2 text-sm">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <span className="font-medium">
                  Motivo: {InactivationReasonLabels[patient.inactivationReason]}
                </span>
              </div>
              {patient.inactivationNotes && (
                <p className="text-sm text-muted-foreground">{patient.inactivationNotes}</p>
              )}
              {patient.inactivatedAt && (
                <p className="text-xs text-muted-foreground">
                  Inativado em: {format(new Date(patient.inactivatedAt), "dd/MM/yyyy 'as' HH:mm", { locale: ptBR })}
                </p>
              )}
            </div>
          )}

          {/* Data de obito */}
          {patient.deceasedAt && (
            <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg">
              <Skull className="h-4 w-4 text-red-600" />
              <span className="text-sm text-red-700">
                Obito: {format(new Date(patient.deceasedAt), "dd/MM/yyyy", { locale: ptBR })}
              </span>
            </div>
          )}

          {/* Status de comunicacao */}
          {patient.canReceiveCommunications === false && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <UserX className="h-4 w-4" />
              <span>Nao recebe comunicacoes automaticas</span>
            </div>
          )}

          {/* Acoes */}
          <div className="flex flex-wrap gap-2 pt-2">
            {canBeInactivated && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowInactivateDialog(true)}
                >
                  <UserX className="mr-2 h-4 w-4" />
                  Inativar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:text-red-700"
                  onClick={() => setShowDeceasedDialog(true)}
                >
                  <Skull className="mr-2 h-4 w-4" />
                  Registrar Obito
                </Button>
              </>
            )}

            {canBeDischarged && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDischargeDialog(true)}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Desligar (Alta)
              </Button>
            )}

            {canBeReactivated && (
              <Button
                variant="default"
                size="sm"
                onClick={() => setShowReactivateConfirm(true)}
              >
                <UserCheck className="mr-2 h-4 w-4" />
                Reativar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Dialog de Inativacao */}
      <Dialog open={showInactivateDialog} onOpenChange={setShowInactivateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Inativar Paciente</DialogTitle>
            <DialogDescription>
              Informe o motivo da inativacao. O paciente nao recebera mais comunicacoes automaticas.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Motivo *</Label>
              <Select
                value={inactivationReason}
                onValueChange={(v) => setInactivationReason(v as InactivationReason)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o motivo" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(InactivationReasonLabels)
                    .filter(([key]) => key !== 'DECEASED')
                    .map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Observacoes</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Observacoes adicionais..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInactivateDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => inactivateMutation.mutate({ reason: inactivationReason, notes })}
              disabled={inactivateMutation.isPending}
            >
              {inactivateMutation.isPending ? (
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Inativar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Obito */}
      <Dialog open={showDeceasedDialog} onOpenChange={setShowDeceasedDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Obito</DialogTitle>
            <DialogDescription>
              Este registro e irreversivel. Confirme os dados antes de prosseguir.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
              <p className="text-sm text-red-800">
                Atencao: Apos registrar o obito, o paciente sera permanentemente inativado e nao podera ser reativado.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Data do Obito</Label>
              <Input
                type="date"
                value={deceasedDate}
                onChange={(e) => setDeceasedDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="space-y-2">
              <Label>Observacoes</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Observacoes..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeceasedDialog(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => markDeceasedMutation.mutate({
                deceasedAt: deceasedDate || undefined,
                notes: notes || undefined,
              })}
              disabled={markDeceasedMutation.isPending}
            >
              {markDeceasedMutation.isPending ? (
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Confirmar Obito
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Desligamento */}
      <Dialog open={showDischargeDialog} onOpenChange={setShowDischargeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Desligar Paciente (Alta)</DialogTitle>
            <DialogDescription>
              O paciente sera desligado do programa com alta. Ele podera ser reativado posteriormente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Observacoes</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Motivo do desligamento, condicoes da alta..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDischargeDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => dischargeMutation.mutate({ notes: notes || undefined })}
              disabled={dischargeMutation.isPending}
            >
              {dischargeMutation.isPending ? (
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Desligar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmacao de Reativacao */}
      <AlertDialog open={showReactivateConfirm} onOpenChange={setShowReactivateConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reativar Paciente</AlertDialogTitle>
            <AlertDialogDescription>
              O paciente sera reativado no programa e voltara a receber comunicacoes automaticas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => reactivateMutation.mutate()}
              disabled={reactivateMutation.isPending}
            >
              {reactivateMutation.isPending ? 'Reativando...' : 'Reativar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
